import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage, FormattedNumber, FormattedDate } from "react-intl";
import { NavLink } from "react-router-dom";

import routes, { routeWithParams } from "../../routes";
import { Icon } from "../../components";
import { titleImage, currencyFormat } from "../../utils/formatters";
import ProviderInfo from "../../components/ProviderInfo";
import wordmark from "../../assets/kikudoo-wordmark-desat.png";

const Sheet = styled.section`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 13;
  padding: 0 0.5rem calc(1rem + env(safe-area-inset-bottom));
  pointer-events: none;
  animation: quickViewIn 0.28s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes quickViewIn {
    from {
      opacity: 0;
      transform: translateY(1.5rem);
    }
  }

  @media (${props => props.theme.desktopScreen}) {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: 30rem;
    padding-bottom: 1.5rem;
  }
`;

/* One card per offering, snapped so a swipe lands on exactly one. */
const Track = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  pointer-events: all;
  -webkit-overflow-scrolling: touch;

  /* overflow-x: auto computes overflow-y to auto as well, which would clip the cards'
     shadow. The padding gives it room inside the scrollport; the margin takes the space
     back off the layout. */
  padding: 0.75rem 0;
  margin: -0.75rem 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.article`
  position: relative;
  flex: 0 0 100%;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.borderRadiusMedium};
  background-color: ${props => props.theme.white};
  box-shadow: 0 0.35rem 0.9rem rgba(0, 0, 0, 0.22);
  overflow: hidden;
`;

const Media = styled.div`
  position: relative;
  width: 100%;
  background-color: ${props => props.theme.gray100};
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
  flex: none;

  &:after {
    content: "";
    display: block;
    padding-top: 75%;
  }
`;

const Kind = styled.span`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background-color: ${props => props.theme.white};
  color: ${props => props.theme.info};
  font-size: 0.6875rem;
  font-weight: 700;
`;

const Close = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.92);
  color: ${props => props.theme.dark};
  cursor: pointer;
`;

const Body = styled.div`
  padding: 0.75rem 0.875rem 0.875rem;
`;

const Title = styled.h3`
  margin: 0 0 0.15rem;
  font-size: 1rem;
  line-height: 1.25;
  color: ${props => props.theme.dark};
`;

const Sub = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  color: ${props => props.theme.secondary};
`;

const ProviderBadge = styled.div`
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  max-width: calc(100% - 1rem);
  padding: 0.25rem 0.75rem 0.25rem 0.25rem;
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.white};
  box-shadow: 0 0.2rem 0.5rem rgba(0, 0, 0, 0.18);
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: bold;
`;

const Facts = styled.ul`
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  color: ${props => props.theme.dark};

  li {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Action = styled(NavLink)`
  flex: ${props => (props.primary ? 2 : 1)};
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${({ primary, theme }) =>
    primary
      ? `
    background-color: ${theme.info};
    color: ${theme.white};
    box-shadow: 0 0.4rem 0.9rem -0.2rem ${theme.info};
  `
      : `
    background-color: ${theme.gray100};
    color: ${theme.dark};
  `}

  &:hover {
    transform: translateY(-1px);
  }
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 0.5rem;
  pointer-events: all;
`;

const Dot = styled.button`
  width: ${props => (props.active ? "1.25rem" : "0.4rem")};
  height: 0.4rem;
  padding: 0;
  border: none;
  border-radius: 999px;
  background-color: ${props => (props.active ? props.theme.white : "rgba(255,255,255,0.55)")};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: width 0.2s ease, background-color 0.2s ease;
`;

const isProvider = result => (result.modelName || "").toLowerCase() === "provider";

const providerOf = result => (isProvider(result) ? result : result.provider || {});

const imageFor = result => {
  const image = titleImage(result, "display");

  return image || providerOf(result).logo || wordmark;
};

const QuickView = ({ location, onClose }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const results = (location && location.results) || [];

  // A different pin starts its stack from the first card again.
  useEffect(() => {
    setActive(0);
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: 0 });
    }
  }, [location]);

  if (results.length < 1) {
    return null;
  }

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    setActive(Math.round(track.scrollLeft / track.clientWidth));
  };

  const goTo = index => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
  };

  return (
    <Sheet>
      <Track ref={trackRef} onScroll={onScroll}>
        {results.map((result, index) => {
          const provider = providerOf(result);
          const providerPath = routeWithParams(routes.provider, { slug: provider.slug });

          return (
            <Card key={`${result.modelName}-${result.id}`}>
              <Media src={imageFor(result)}>
                <Kind>
                  <FormattedMessage id={isProvider(result) ? "provider.one" : "course.one"} />
                </Kind>
                {index === active && (
                  <Close onClick={onClose} aria-label="close">
                    <Icon name="times" size="small" />
                  </Close>
                )}
                {!isProvider(result) && provider.slug && (
                  <ProviderBadge>
                    <ProviderInfo provider={provider} compact />
                  </ProviderBadge>
                )}
              </Media>

              <Body>
                <Title>{result.name}</Title>
                {isProvider(result) && result.subTitle && <Sub>{result.subTitle}</Sub>}

                <Facts>
                  {result.distance > 0 && (
                    <li>
                      <Icon name="location-dot" size="small" />
                      <FormattedMessage
                        id="pages.SearchMap.kilometers"
                        values={{ distance: Math.round(result.distance * 10) / 10 }}
                      />
                    </li>
                  )}
                  {result.priceFrom > 0 && (
                    <li>
                      <Icon name="tag" size="small" />
                      <FormattedNumber value={result.priceFrom / 100} {...currencyFormat(result)} />
                    </li>
                  )}
                  {result.startDate && (
                    <li>
                      <Icon name="calendar" size="small" />
                      <FormattedDate value={result.startDate} day="numeric" month="short" />
                    </li>
                  )}
                  {result.courseCount > 0 && (
                    <li>
                      <Icon name="list" size="small" />
                      <FormattedMessage
                        id="pages.SearchMap.courseCount"
                        values={{ count: result.courseCount }}
                      />
                    </li>
                  )}
                </Facts>

                <Actions>
                  {isProvider(result) ? (
                    <Action primary to={providerPath}>
                      <FormattedMessage id="pages.SearchMap.cta.provider" />
                      <Icon name="arrow-right" size="small" />
                    </Action>
                  ) : (
                    <>
                      <Action
                        primary
                        to={routeWithParams(routes.course, {
                          slug: provider.slug,
                          id: result.id,
                        })}
                      >
                        <FormattedMessage id="pages.SearchMap.cta.course" />
                        <Icon name="arrow-right" size="small" />
                      </Action>
                      <Action to={providerPath}>
                        <FormattedMessage id="pages.SearchMap.cta.providerShort" />
                      </Action>
                    </>
                  )}
                </Actions>
              </Body>
            </Card>
          );
        })}
      </Track>

      {results.length > 1 && (
        <Pager>
          {results.map((result, index) => (
            <Dot
              key={`dot-${result.modelName}-${result.id}`}
              active={index === active}
              onClick={() => goTo(index)}
              aria-label={`${index + 1}`}
            />
          ))}
        </Pager>
      )}
    </Sheet>
  );
};

QuickView.propTypes = {
  location: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

QuickView.defaultProps = {
  location: null,
};

export default QuickView;
